import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Briefcase, UserRound, AlertCircle, LockKeyhole } from 'lucide-react';
import { API_BASE_URL } from "../config/apiconfig";
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const CommonOnboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const email = location.state?.email || user?.email || '';

  const [role, setRole] = useState('user');
  const [adminCode, setAdminCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Guard: if onboarding already started or completed in DB, redirect away and replace history
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const targetEmail = user?.email || email;
      if (!targetEmail) return;
      try {
        const res = await fetch(`${API_BASE_URL}/profile/${targetEmail}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const startedOrCompleted = (Number(data.onboarding_step || 0) >= 1) || (Number(data.onboarding_completed || 0) === 1);
        if (!cancelled && startedOrCompleted) {
          const to = user?.role === 'recruiter'
            ? '/recruiter-onboarding'
            : (user?.role === 'e2e-admin' ? '/admin-dashboard' : '/onboarding');
          navigate(`${to}?onboarded=1`, { replace: true });
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, email, navigate]);

  // Match animation style used in Onboarding.jsx
  const stepVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Missing email. Please sign up again.');
      return;
    }

    if (role === 'e2e-admin' && adminCode !== '201129') {
      setError('Invalid admin access code.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/select-role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role, adminCode })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to set role.');
        setIsLoading(false);
        return;
      }
      // If API returns a fresh token and user object, persist them
      if (data?.token) {
        localStorage.setItem('token', data.token);
      }
      const updatedUser = data?.user ? data.user : (user ? { ...user, role, onboarding_step: 1, onboarding_completed: 0 } : null);
      if (updatedUser) {
        login(updatedUser);
      }
      // Navigate to the appropriate onboarding page
      if (role === 'recruiter') {
        navigate('/recruiter-onboarding', { replace: true });
      } else if (role === 'e2e-admin') {
        navigate('/admin-dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-800">
      <Header />
      <main className="flex-1 flex items-center justify-center py-10">
        <div className="w-full max-w-2xl mx-auto px-4">
          <AnimatePresence mode="wait">
            <motion.section
              key="common-onboard"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={stepVariants}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
                <div className="px-8 py-6 border-b border-slate-200 flex items-center gap-3">
                  <div>
                    <h1 className="text-xl font-semibold">Choose how you'll use E2E</h1>
                    <p className="text-sm text-slate-600">Select your role to start the tailored onboarding</p>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button type="button" onClick={() => setRole('user')} className={`p-4 rounded-xl border-2 transition ${role==='user'?'border-blue-600 bg-blue-50':'border-slate-200 hover:border-slate-300'}`}>
                      <UserRound className="w-6 h-6 mb-2" />
                      <div className="font-medium">Job Seeker</div>
                      <div className="text-xs text-slate-600">Find jobs faster, create a strong profile, and track applications in one place.</div>
                    </button>
                    <button type="button" onClick={() => setRole('recruiter')} className={`p-4 rounded-xl border-2 transition ${role==='recruiter'?'border-blue-600 bg-blue-50':'border-slate-200 hover:border-slate-300'}`}>
                      <Briefcase className="w-6 h-6 mb-2" />
                      <div className="font-medium">Recruiter</div>
                      <div className="text-xs text-slate-600">Post jobs, review candidates, and manage hiring workflows from your dashboard.</div>
                    </button>
                    <button type="button" onClick={() => setRole('e2e-admin')} className={`p-4 rounded-xl border-2 transition ${role==='e2e-admin'?'border-blue-600 bg-blue-50':'border-slate-200 hover:border-slate-300'}`}>
                      <Shield className="w-6 h-6 mb-2" />
                      <div className="font-medium">E2E Admin</div>
                      <div className="text-xs text-slate-600">Manage system settings, users, and platform-wide data (restricted access).</div>
                    </button>
                  </div>

                  {role === 'e2e-admin' && (
                    <div className="relative">
                      <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="password"
                        placeholder="Admin Access Code"
                        value={adminCode}
                        onChange={(e) => setAdminCode(e.target.value)}
                        className="w-full pl-10 pr-3 py-3 rounded-lg border-2 border-slate-200 focus:border-blue-600 outline-none"
                      />
                    </div>
                  )}

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button type="submit" disabled={isLoading} className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-70">
                      {isLoading ? 'Saving...' : 'Continue'}
                    </button>
                  </div>

                  {!email && (
                    <p className="text-xs text-slate-500">No email provided from signup. You may need to restart the signup process.</p>
                  )}
                </form>
              </div>
            </motion.section>
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CommonOnboard;
